import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    Inject,
    input,
    OnInit,
    PLATFORM_ID,
    RendererStyleFlags2,
    ViewChild,
    ViewRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { Renderer } from '@df/ng/renderer';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Wishlist, WISHLIST_ADD_SUCCESS_EVENT, WISHLIST_REMOVE_SUCCESS_EVENT } from '@df/session/api/wishlist/wishlist';
import { TranslationService } from '@df/ui/translation/translation.service';
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { take } from 'rxjs/internal/operators/take';

@Component({
    selector: '[wishlistQty]',
    templateUrl: './wishlist-qty.html',
    styleUrl: './wishlist-qty.scss',
    standalone: true,
    imports: [RouterModule],
    host: {
        '[class]': 'classNames()',
        '[class.in-wishlist]': 'inWishlist',
        '[class.not-in-wishlist]': '!inWishlist',
        'rel': 'nofollow',
        'id': 'wishlist-qty',
        '[attr.aria-label]': 'ariaLabel'
    },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WishlistQtyComponent implements OnInit {
    readonly className = input<string>('', { alias: 'class' });

    protected readonly classNames = computed(() => this.className() + ' ' + 'pos-relative');

    @ViewChild('iconRef', { read: ElementRef }) iconRef!: ElementRef;
    @ViewChild('iconHeartRef', { read: ElementRef }) iconHeartRef!: ElementRef;

    protected inWishlist = false;
    protected ariaLabel = '';

    protected tween!: gsap.core.Timeline;
    protected elementRef = inject(ElementRef);
    protected destroyRef = inject(DestroyRef);
    protected wishlist = inject(Wishlist);
    protected readonly isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    protected timeoutService = injectTimeoutService();
    protected translationService = inject(TranslationService);
    protected dfEventService = inject(DfEventService);

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected meta: Meta,
        protected events: Events,
        protected renderer: Renderer,
        protected app: App,
        protected client: Client,
        @Inject(DOCUMENT) protected docRef: Document
    ) {
    }

    ngOnInit() {
        this.registerGsapAnimation();

        this.meta.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onMetaChange());
        this.wishlist.wishlistUpdateSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.setInWishlist(!!this.wishlist.items?.length, 2000);
        });

        this.events
            .on(WISHLIST_ADD_SUCCESS_EVENT)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(data => {
                this.onAdd(data.product);
            });

        this.events
            .on(WISHLIST_REMOVE_SUCCESS_EVENT)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.setInWishlist());

        this.events
            .on('wishlist-toggle.product.change')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ productId }) => this.onMetaChange(productId));


        this.dfEventService.success(event => {
            if (event.id === 'wishlistDataChange') {
                this.updateAriaLabel();
            }
        });

        this.updateAriaLabel();
    }

    protected onAdd(productId: number) {
        if (this.app.isPlatformBrowser) {
            this.tween?.progress(0)?.kill();
            this.init();
            const button = this.elementRef.nativeElement;
            const icon = this.iconRef.nativeElement;
            const iconRect = icon.getBoundingClientRect();
            const iconHeart = this.iconHeartRef.nativeElement;
            let wishlistIcon = this.docRef.getElementById('wishlist-toggle-icon-' + productId);

            if (!wishlistIcon) {
                const product = this.app.resources.product.getExisting(productId);
                wishlistIcon = this.docRef.getElementById('wishlist-toggle-icon-' + product?.parent?.id);
            }

            if (!wishlistIcon) {
                return;
            }

            const wishlistIconRect = wishlistIcon.getBoundingClientRect();

            // sticky wrapper
            const headerIcons = this.docRef.getElementById('header-icons');
            let headerLeft = iconRect.x;

            if (headerIcons && headerIcons.classList.contains('is-sticky')) {
                const headerIconsRect = headerIcons.getBoundingClientRect();
                headerLeft = iconRect.x - headerIconsRect.x;
            }

            const targetX = wishlistIconRect.x - iconRect.x;
            const targetY = wishlistIconRect.y - iconRect.y;
            this.tween = gsap.timeline();
            const path = [
                { x: targetX, y: targetY },
                { x: targetX - targetY / 10, y: targetY - targetY / 10 },
                { x: targetX - targetY / 10, y: targetY / 10 },
                { x: 0, y: 0 }
            ];

            this.renderer.removeClass(iconHeart, 'ng-hide');
            this.renderer.setStyle(button, '--wishlist-icon-x', `${headerLeft}px`, RendererStyleFlags2.DashCase);
            this.renderer.setStyle(button, '--wishlist-icon-y', `${iconRect.y}px`, RendererStyleFlags2.DashCase);

            this.tween
                .to(iconHeart, {
                    duration: 0,
                    scale: 1
                })
                .to(iconHeart, {
                    duration: 1.5,
                    ease: 'power2.out',
                    motionPath: {
                        path,
                        type: 'cubic'
                    }
                })
                .to(iconHeart, {
                    delay: 0.5,
                    duration: 1,
                    scale: 0
                })
                .then(() => {
                    this.renderer.addClass(iconHeart, 'ng-hide');
                    this.tween.progress(0).kill();
                    this.renderer.addClass(wishlistIcon, 'will-animate');

                    this.setInWishlist(true);
                });

            this.client.obs.scroll.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                this.tween.progress(1);
            });
        }
    }

    protected onMetaChange(productId?: number) {
        if (this.meta?.model?.type === 'product') {
            const _productId = Number(productId || this.meta.model.id);

            this.setInWishlist(!!this.wishlist.getItemsByProduct(_productId)?.length);
        } else {
            this.setInWishlist();
        }
    }

    protected setInWishlist(value = !!this.wishlist.items?.length, timeout = 0) {
        this.timeoutService.setTimeout(() => {
            this.inWishlist = value;

            this.detectChanges();
        }, timeout);
    }

    protected detectChanges() {
        this.timeoutService.async(() => {
            if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    protected registerGsapAnimation() {
        if (this.isPlatformBrowser) {
            gsap.registerPlugin(MotionPathPlugin);
        }
    }

    private init() {
        const icon = this.iconRef.nativeElement;

        if (icon) {
            const iconRect = icon.getBoundingClientRect();
            const button = this.elementRef.nativeElement;

            this.renderer.setStyle(button, '--wishlist-icon-x', `${iconRect.x}px`, RendererStyleFlags2.DashCase);
            this.renderer.setStyle(button, '--wishlist-icon-y', `${iconRect.y}px`, RendererStyleFlags2.DashCase);
        }
    }

    private updateAriaLabel() {
        this.ariaLabel = `${this.translationService.transform('Wishlist quantity %', this.wishlist.qty || 0)} ${this.translationService.transform('click to open minibasket')}`;
    }
}
